import { ContextMenu } from "@nova/component-context-menu"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { setSelectedTool } from "plugins/Toolbox/actions"
import { selectIconTemplate, selectSelectedToolEntity } from "plugins/Toolbox/tool-registry"
import React, { useCallback, useEffect, useMemo, useState } from "react"
import { selectActivatedFireId, selectClipboardMapIcon } from "../control/selectors"
import { setClipboardMapIcon } from "../control/slice"
import { setGlobalNotificationContext } from "user/profile/slice"
import { GlobalNotificationStatus } from "@nova/component-global-notification"
import * as Icons from "@nova/component-icons"
import { useWorkspacePermissions } from "features/data-management/workspace/useWorkspacePermissions"
import { ContextMenuPosition } from "../types"
import { createIcon, deleteIcon } from "features/pubsub/entityActions/cubes/icon"
import { Coordinate, Icon } from "@nova/protobuf"
import { selectUserID } from "user/profile/selectors"
import { selectNextIconNumber } from "features/entity-caching/icons/selectors"
import { convertToFormattedDm, convertToFormattedDms } from "@nova/calculate-geospatial"
import { config } from "@nova/configuration/browser"
import { Marker } from "react-map-gl"

export const convertToW3W = async (dd: Coordinate) => {
    const apiKey = config.get("what3WordsApiKey")
    try {
        const response = await fetch(
            `https://api.what3words.com/v3/convert-to-3wa?key=${apiKey}&coordinates=${dd.latitude}%2C${dd.longitude}&language=en&format=json`
        )
        if (!response.ok) {
            return ""
        }
        const json = await response.json()
        return `///${json.words}`
    } catch (e) {
        console.error(`Failed to get w3w for coordinate with error: ${e}`)
        return ""
    }
}

type MapContextMenuContainerProps = {
    position: ContextMenuPosition
    setPosition: (position?: ContextMenuPosition) => void
    mapLayer?: string
}

/**
 * Sets up the menu options for the maps right click menu
 */
export const MapContextMenuContainer = ({
    position,
    setPosition,
    mapLayer,
}: MapContextMenuContainerProps) => {
    const dispatch = useAppDispatch()
    const { isEditor } = useWorkspacePermissions()
    const fireId = useAppSelector(selectActivatedFireId)
    const activeIcon = useAppSelector(selectSelectedToolEntity())
    const clipboardIcon = useAppSelector(selectClipboardMapIcon)
    const { organizationSettingNotation } = config.use("organizationSettingNotation") as {
        organizationSettingNotation: "decimal" | "degreesMinutes" | "dms"
    }

    const userId = useAppSelector(selectUserID)

    const iconCoordinate: Coordinate | undefined = activeIcon as Icon

    const coords = useMemo(
        () => position.coordinates || iconCoordinate,
        [position.coordinates, iconCoordinate]
    )
    const formattedCoordinatesRounded = useMemo(() => {
        if (!coords) return
        switch (organizationSettingNotation) {
            case "decimal":
                return `${coords.latitude.toFixed(4)}\u00B0, ${coords.longitude.toFixed(4)}\u00B0`
            case "degreesMinutes":
                return `${convertToFormattedDm(coords.latitude, false, false)}, ${convertToFormattedDm(coords.longitude, true, false)}`
            case "dms":
                return `${convertToFormattedDms(coords.latitude, false, false)}, ${convertToFormattedDms(coords.longitude, true, false)}`
        }
    }, [coords, organizationSettingNotation])
    const formattedCoordinatesNotRounded = useMemo(() => {
        if (!coords) return
        switch (organizationSettingNotation) {
            case "decimal":
                return `${coords.latitude.toFixed(6)}\u00B0, ${coords.longitude.toFixed(6)}\u00B0`
            case "degreesMinutes":
                return `${convertToFormattedDm(coords.latitude, false, true)}, ${convertToFormattedDm(coords.longitude, true, true)}`
            case "dms":
                return `${convertToFormattedDms(coords.latitude, false, true)}, ${convertToFormattedDms(coords.longitude, true, true)}`
        }
    }, [coords, organizationSettingNotation])
    const [w3wCoordinates, setW3WCoordinates] = useState("")
    useEffect(() => {
        ;(async () => {
            if (!coords) return
            const w3w = await convertToW3W(coords)
            setW3WCoordinates(w3w)
        })()
    }, [coords])

    const handleIconDelete = useCallback(() => {
        if (!activeIcon || !fireId) {
            return
        }
        dispatch(deleteIcon({ id: activeIcon.id }))
        dispatch(setSelectedTool(undefined))
        setPosition(undefined)
    }, [activeIcon, fireId, dispatch, setPosition])

    const handleIconCopy = useCallback(() => {
        if (!activeIcon) {
            return
        }
        dispatch(setClipboardMapIcon(activeIcon))
        setPosition(undefined)
    }, [activeIcon, dispatch, setPosition])

    const handleIconCut = useCallback(() => {
        handleIconCopy()
        handleIconDelete()
    }, [handleIconCopy, handleIconDelete])

    const handleIconPaste = useCallback(() => {
        if (!clipboardIcon || !userId || !position.coordinates) {
            return
        }

        dispatch(
            createIcon({
                data: {
                    url: clipboardIcon.url,
                    name: clipboardIcon.name,
                    color: clipboardIcon.color,
                    longitude: position.coordinates.longitude,
                    latitude: position.coordinates.latitude,
                    createdBy: {
                        userId,
                    },
                },
            })
        )
        setPosition(undefined)
    }, [clipboardIcon, dispatch, position.coordinates, setPosition, userId])

    const handleCoordinateCopy = useCallback(
        async (format: "orgSettings" | "W3W") => {
            try {
                if (!coords) throw "No coordinates"

                let textToCopy = ""
                switch (format) {
                    case "orgSettings":
                        textToCopy = formattedCoordinatesNotRounded!
                        break
                    case "W3W":
                        textToCopy = w3wCoordinates
                        break
                    default:
                        throw "Invalid coordinate format"
                }

                await navigator.clipboard.writeText(textToCopy)
                dispatch(
                    setGlobalNotificationContext({
                        message: "Copied coordinates.",
                        status: GlobalNotificationStatus.success,
                        timeoutInMs: 3000,
                    })
                )
            } catch {
                dispatch(
                    setGlobalNotificationContext({
                        message: "Could not copy coordinates.",
                        status: GlobalNotificationStatus.error,
                        timeoutInMs: 3000,
                    })
                )
            }
            setPosition(undefined)
        },
        [setPosition, coords, dispatch, formattedCoordinatesNotRounded, w3wCoordinates]
    )

    const nextIconNumber = useAppSelector(selectNextIconNumber)
    const iconTemplate = useAppSelector(selectIconTemplate)
    const createNewPoint = useCallback(() => {
        setPosition(undefined)
        if (!position.coordinates || !userId) return
        dispatch(
            createIcon({
                data: {
                    longitude: position.coordinates.longitude,
                    latitude: position.coordinates.latitude,
                    url: iconTemplate.url,
                    color: iconTemplate.color,
                    name:
                        iconTemplate.name === "Point"
                            ? `Point ${nextIconNumber}`
                            : iconTemplate.name,
                    createdBy: {
                        userId,
                    },
                },
            })
        )
    }, [
        dispatch,
        userId,
        position.coordinates,
        iconTemplate.url,
        iconTemplate.color,
        iconTemplate.name,
        nextIconNumber,
        setPosition,
    ])

    //rebuild the available menu options depending on what map layer was clicked
    const menuOptions = useMemo(() => {
        const buildMenuOptions = () => {
            const viewerAllowedOptions = []
            if (coords) {
                viewerAllowedOptions.push({
                    label: formattedCoordinatesRounded!,
                    onClick: () => handleCoordinateCopy("orgSettings"),
                    Icon: Icons.Copy,
                })
                viewerAllowedOptions.push({
                    label: w3wCoordinates,
                    onClick: () => handleCoordinateCopy("W3W"),
                    Icon: Icons.Copy,
                })
            }

            if (!isEditor) return viewerAllowedOptions

            if (mapLayer === "map-pano-icons") return viewerAllowedOptions

            const baseOptions = []
            if (clipboardIcon) baseOptions.push({ label: "Paste", onClick: handleIconPaste })

            // If right clicked on map itself...
            if (position.coordinates)
                return [
                    ...viewerAllowedOptions,
                    ...baseOptions,
                    {
                        label: "Place New Point",
                        onClick: createNewPoint,
                        Icon: Icons.AddLocation,
                    },
                ]

            if (mapLayer === "map-icons") {
                return [
                    ...viewerAllowedOptions,
                    { label: "Copy", onClick: handleIconCopy },
                    { label: "Cut", onClick: handleIconCut },
                    ...baseOptions,
                    {
                        label: "Delete",
                        onClick: handleIconDelete,
                        style: { color: "rgba(241, 93, 79, 1)" },
                    },
                ]
            }
            return baseOptions
        }
        return buildMenuOptions()
    }, [
        coords,
        isEditor,
        mapLayer,
        clipboardIcon,
        handleIconPaste,
        position.coordinates,
        createNewPoint,
        formattedCoordinatesRounded,
        w3wCoordinates,
        handleCoordinateCopy,
        handleIconCopy,
        handleIconCut,
        handleIconDelete,
    ])

    if (menuOptions.length === 0) {
        return null
    }

    return (
        <>
            <ContextMenu
                mousePosition={position}
                setMousePosition={(position) =>
                    setPosition(position ? { ...position, isMap: false } : undefined)
                }
                menuOptions={menuOptions}
            />
            <Marker longitude={coords.longitude} latitude={coords.latitude}>
                <div className="w-2 h-2 bg-yellow-400 rotate-45 cursor-default" />
            </Marker>
        </>
    )
}
